// Wires the typed event bus to shell store side-effects (§12.4). Navigation
// events that need the router are handled in the Layout; store-level effects
// live here. Call initShellEffects() once at boot.

import { shellEventBus } from './eventBus';
import { useShellStore } from '../store/shellStore';
import type { ShellEvent } from '@growthaxl/web-foundation';

let initialized = false;
const cleanupFns: Array<() => void> = [];

/** Test-only reset so each test file can re-wire subscribers. */
export function resetShellEffectsForTests(): void {
  initialized = false;
  for (const fn of cleanupFns) fn();
  cleanupFns.length = 0;
}

/**
 * DR-13: navigation is deferred via queueMicrotask so the
 * CanvasOverlayManager (which also subscribes to SOLUTION_RUN_INITIATED)
 * completes its synchronous cleanup (store.reset, setActiveOverlay(null))
 * before the canvas re-renders under a navigation event.
 *
 * Always switches to the 'runs' capability tab. If the backend returned a
 * runId, also dispatches OPEN_RUN_DETAIL so the runs canvas opens that
 * specific run (optimistic: tab switch happens regardless of runId).
 */
function handleSolutionRunInitiated(
  event: Extract<ShellEvent, { type: 'SOLUTION_RUN_INITIATED' }>
): void {
  const { runId } = event.payload;

  queueMicrotask(() => {
    // Always navigate to the Runs capability tab.
    shellEventBus.dispatch({
      type: 'SWITCH_CAPABILITY',
      payload: { capabilityId: 'runs' },
    });

    // Open the specific run detail only when the backend returned an ID.
    if (runId) {
      shellEventBus.dispatch({
        type: 'OPEN_RUN_DETAIL',
        payload: { runId },
      });
    }
  });
}

export function initShellEffects(): void {
  if (initialized) return;
  initialized = true;

  cleanupFns.push(shellEventBus.on('SOLUTION_RUN_INITIATED', handleSolutionRunInitiated));

  cleanupFns.push(shellEventBus.subscribe(event => {
    const store = useShellStore.getState();
    switch (event.type) {
      case 'ESA_OPEN_PANEL':
        store.setEsaPanelOpen(true);
        break;
      case 'ESA_CLOSE_PANEL':
        store.setEsaPanelOpen(false);
        break;
      case 'SOLUTION_CONTEXT_CHANGED':
        store.setSolutionContext(event.payload.solutionId);
        break;
      case 'SWITCH_CAPABILITY':
        store.setActiveCapability(event.payload.capabilityId);
        break;
      case 'NAVIGATE_TO_WORKSPACE':
        store.setActiveWorkspace(event.payload.workspaceId as Parameters<typeof store.setActiveWorkspace>[0]);
        break;
      case 'NAVIGATE_TO_CANVAS':
        store.setActiveCanvas(event.payload.canvasId as Parameters<typeof store.setActiveCanvas>[0]);
        break;
      default:
        break;
    }
  }));
}
